import { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { addToCart, updateCartItemQuantity } from '../../actions/cart'
import { RESOURCE_RESTRAINED_HARDWARE } from '../../VNMode/Utils'
import { bridge_getDeviceModel } from '../../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'

const THROTTLING_TIME = 70 // in milliseconds
const THROTTLING_LONGER_TIME = 110

/**
 * Custom hook for handling cart item actions with throttling.
 * Relies on useRef for throttling and tracking state without additional renders.
 *
 * @param {Object} param - The parameters object.
 * @param {Object} param.throttlingRef - tracks the throttling timer
 * @param {Object} param.throttlingActiveRef - tracks if throttling is active
 * @param {Object} param.isInCartRef - tracks if the item is in the cart.
 * @param {Object} param.latestQuantityRef - tracks the latest quantity of the item.
 * @returns {Object}
 */
const useCartItem = ({ throttlingRef, throttlingActiveRef, isInCartRef, latestQuantityRef }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const isResourceRestrained = useMemo(() => RESOURCE_RESTRAINED_HARDWARE?.includes(bridge_getDeviceModel()), [])

  /**
   * Add item to cart with required modifiers
   * this adds the item to cart, then opens Item Details
   */
  const addItemWithModifiers = useCallback(
    ({ itemId }) => {
      history.push({ pathname: `/concession-order/item-detail/${itemId}` })
    },
    [history],
  )

  /**
   * Add or increment item in cart with throttling for consecutive taps
   * this updates refs to avoid having to wait for state updates
   */
  const upsertItem = useCallback(
    ({ itemId, max, isAlcohol, cartIndex, hasRequiredModifiers = false, defaultModifiers = [] }) => {
      // another tap is registered, clear timer
      if (throttlingRef?.current) {
        clearTimeout(throttlingRef?.current)
      }

      // start a new timer
      throttlingActiveRef.current = true
      throttlingRef.current = setTimeout(
        () => {
          throttlingActiveRef.current = false

          // has modifiers
          if (hasRequiredModifiers) return

          // add to cart
          if (!isInCartRef?.current && cartIndex === -1) {
            dispatch(addToCart({ itemId, modifiers: defaultModifiers, quantity: latestQuantityRef?.current || 1, isAlcohol }))
            isInCartRef.current = true
            return
          }

          // increment quantity
          if (latestQuantityRef?.current >= max) {
            dispatch(updateCartItemQuantity({ index: cartIndex, quantity: max }))
            return
          }

          dispatch(updateCartItemQuantity({ index: cartIndex, quantity: latestQuantityRef?.current ?? 1 }))
          return
        },
        isResourceRestrained ? THROTTLING_LONGER_TIME : THROTTLING_TIME,
      )
    },
    [dispatch, isInCartRef, isResourceRestrained, latestQuantityRef, throttlingActiveRef, throttlingRef],
  )

  return { addItemWithModifiers, upsertItem }
}

export default useCartItem
